import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import useSearchState, { UseSearchStateReturn } from "@hooks/useSearchState"

import { loadGoogleMapsPlaces } from "@utils/googleMaps"

import { SelectSearchData } from "@molecules/Select/Select.context"

export interface PlacesSuggestion {
    placeId: string
    primaryText: string
    secondaryText: string
    fullText: string
}

export interface ParsedAddress {
    street: string
    city: string
    state: string
    postalCode: string
    country: string
    formattedAddress: string
}

const DEFAULT_DEBOUNCE_MS = 250
const DEFAULT_MIN_QUERY_LENGTH = 3
const DEFAULT_COUNTRY_RESTRICTIONS = ["us", "ca"] as const
const DEFAULT_INCLUDED_PRIMARY_TYPES = ["street_address", "premise", "subpremise", "route"] as const

export interface UsePlacesAutocompleteOptions {
    /** Debounce delay in milliseconds before firing a request. Defaults to 250. */
    debounce?: number
    /** Minimum character count before requests fire. Defaults to 3. */
    minQueryLength?: number
    /**
     * ISO 3166-1 alpha-2 region codes to restrict suggestions to. Defaults to
     * `["us", "ca"]`. Pass an empty array to accept all regions.
     */
    countryRestrictions?: string[]
    /**
     * Place types to include. Defaults to street-address-shaped types
     * (`street_address`, `premise`, `subpremise`, `route`). Pass an empty array to
     * accept all types.
     */
    includedPrimaryTypes?: string[]
}

export interface UsePlacesAutocompleteReturn extends UseSearchStateReturn {
    /** Resolved suggestions for the latest debounced query. */
    suggestions: PlacesSuggestion[]
    /** True while a fetch is in flight. */
    isLoading: boolean
    /** True if the latest request failed. */
    isError: boolean
    /**
     * True once the current debounced query has been resolved — either fetched
     * to completion (success or error) or skipped because it's below
     * `minQueryLength`. Useful for gating "no results" UI so it doesn't flicker
     * during the brief render between debounce-settling and fetch-starting.
     */
    isQueryReady: boolean
    /**
     * Fetch full details for a chosen suggestion's `placeId` and clear the search
     * state so the next debounce tick won't refetch. Returns the parsed address or
     * `null` if the lookup failed.
     */
    selectSuggestion: (placeId: string) => Promise<ParsedAddress | null>
    /**
     * SelectSearchData-shaped object — pass to `<Select.Root searchData={...}>` so
     * `Select.Loading`, `Select.NoResults`, and `Select.Error` render automatically.
     */
    searchData: SelectSearchData<PlacesSuggestion>
}

function parseAddressComponents(place: google.maps.places.Place, fallbackFormatted: string): ParsedAddress {
    const components = place.addressComponents ?? []
    const findComponent = (type: string) =>
        components.find((component) => component.types.includes(type))?.shortText ?? ""

    const streetNumber = findComponent("street_number")
    const route = findComponent("route")
    const street = [streetNumber, route].filter(Boolean).join(" ")

    return {
        street,
        city: findComponent("locality") || findComponent("postal_town") || findComponent("sublocality"),
        state: findComponent("administrative_area_level_1"),
        postalCode: findComponent("postal_code"),
        country: findComponent("country"),
        formattedAddress: place.formattedAddress ?? fallbackFormatted,
    }
}

export default function usePlacesAutocomplete(
    options: UsePlacesAutocompleteOptions = {},
): UsePlacesAutocompleteReturn {
    const {
        debounce = DEFAULT_DEBOUNCE_MS,
        minQueryLength = DEFAULT_MIN_QUERY_LENGTH,
        countryRestrictions,
        includedPrimaryTypes,
    } = options

    const searchState = useSearchState({ debounce })
    const { searchTerm, debouncedSearchTerm, isTyping, onSearchChange, resetSearch: resetSearchState } = searchState

    const [suggestions, setSuggestions] = useState<PlacesSuggestion[]>([])
    const [isError, setIsError] = useState<boolean>(false)
    const [isFetching, setIsFetching] = useState<boolean>(false)
    // The debounced query that has actually been resolved (fetched, errored, or
    // short-circuited as below min length). Used to distinguish "we just settled
    // the debounce but haven't started fetching yet" from "we have a real result".
    const [resolvedQuery, setResolvedQuery] = useState<string>("")

    const placesLibraryRef = useRef<google.maps.PlacesLibrary | null>(null)
    const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null)
    const requestIdRef = useRef<number>(0)

    // Stable dep keys for arrays so consumers can pass inline literals without thrashing.
    const countryRestrictionsKey = countryRestrictions?.join(",") ?? ""
    const includedPrimaryTypesKey = includedPrimaryTypes?.join(",") ?? ""

    useEffect(() => {
        if (debouncedSearchTerm.length < minQueryLength) {
            setSuggestions([])
            setIsFetching(false)
            setResolvedQuery(debouncedSearchTerm)
            return
        }

        const requestId = ++requestIdRef.current

        const fetchSuggestions = async () => {
            setIsFetching(true)
            setIsError(false)

            try {
                const placesLibrary = placesLibraryRef.current ?? (await loadGoogleMapsPlaces())
                placesLibraryRef.current = placesLibrary

                if (!sessionTokenRef.current) {
                    sessionTokenRef.current = new placesLibrary.AutocompleteSessionToken()
                }

                const primaryTypes = includedPrimaryTypes ?? Array.from(DEFAULT_INCLUDED_PRIMARY_TYPES)
                const regionCodes = countryRestrictions ?? Array.from(DEFAULT_COUNTRY_RESTRICTIONS)

                const request: google.maps.places.AutocompleteRequest = {
                    input: debouncedSearchTerm,
                    sessionToken: sessionTokenRef.current,
                    ...(primaryTypes.length > 0 ? { includedPrimaryTypes: primaryTypes } : {}),
                    ...(regionCodes.length > 0 ? { includedRegionCodes: regionCodes } : {}),
                }

                const { suggestions: rawSuggestions } =
                    await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

                if (requestId !== requestIdRef.current) {
                    return
                }

                const mapped = rawSuggestions
                    .map((suggestion): PlacesSuggestion | null => {
                        const prediction = suggestion.placePrediction
                        if (!prediction) {
                            return null
                        }
                        return {
                            placeId: prediction.placeId,
                            primaryText: prediction.mainText?.text ?? prediction.text.text,
                            secondaryText: prediction.secondaryText?.text ?? "",
                            fullText: prediction.text.text,
                        }
                    })
                    .filter((suggestion): suggestion is PlacesSuggestion => suggestion !== null)

                setSuggestions(mapped)
                setResolvedQuery(debouncedSearchTerm)
            } catch (error) {
                console.warn("usePlacesAutocomplete: failed to fetch suggestions", error)
                if (requestId === requestIdRef.current) {
                    setIsError(true)
                    setSuggestions([])
                    setResolvedQuery(debouncedSearchTerm)
                }
            } finally {
                if (requestId === requestIdRef.current) {
                    setIsFetching(false)
                }
            }
        }

        void fetchSuggestions()
    }, [debouncedSearchTerm, minQueryLength, countryRestrictionsKey, includedPrimaryTypesKey])

    const resetSearch = useCallback(() => {
        // Bump the request ID so any in-flight response is ignored on settle.
        requestIdRef.current++
        setSuggestions([])
        setIsError(false)
        setIsFetching(false)
        setResolvedQuery("")
        resetSearchState()
    }, [resetSearchState])

    const selectSuggestion = useCallback(
        async (placeId: string): Promise<ParsedAddress | null> => {
            const placesLibrary = placesLibraryRef.current
            const suggestion = suggestions.find((candidate) => candidate.placeId === placeId)
            if (!placesLibrary || !suggestion) {
                return null
            }

            // Bump request ID so any in-flight autocomplete response is discarded.
            requestIdRef.current++
            setSuggestions([])
            setResolvedQuery("")
            resetSearchState()
            setIsFetching(true)
            setIsError(false)

            try {
                const place = new placesLibrary.Place({ id: suggestion.placeId })
                await place.fetchFields({ fields: ["addressComponents", "formattedAddress"] })
                sessionTokenRef.current = null
                return parseAddressComponents(place, suggestion.fullText)
            } catch (error) {
                console.warn("usePlacesAutocomplete: failed to fetch place details", error)
                setIsError(true)
                return null
            } finally {
                setIsFetching(false)
            }
        },
        [suggestions, resetSearchState],
    )

    // "Ready" means the current debounced query is long enough AND has actually
    // been resolved (fetched or skipped as below-min). The latter prevents the
    // intermediate render between debounce-settling and fetch-starting from
    // briefly satisfying "no results" UI.
    const isQueryReady = debouncedSearchTerm.length >= minQueryLength && resolvedQuery === debouncedSearchTerm

    const searchData = useMemo<SelectSearchData<PlacesSuggestion>>(
        () => ({
            results: suggestions,
            // Only show first-load skeletons when there's no stale data to render —
            // typing/refetching with existing results uses stale-while-revalidate.
            isLoading: isFetching && suggestions.length === 0,
            isFetching,
            isFetchingNextPage: false,
            isError,
            fetchNextPage: () => Promise.resolve(),
            hasNextPage: false,
            searchState: {
                ...searchState,
                // Gate the "has search term" signal on isQueryReady so Select.NoResults
                // doesn't fire for queries that haven't been searched yet (too short
                // OR debounce just settled but fetch hasn't started).
                hasSearchTerm: isQueryReady,
            },
        }),
        [suggestions, isFetching, isError, searchState, isQueryReady],
    )

    return {
        searchTerm,
        debouncedSearchTerm,
        hasSearchTerm: searchState.hasSearchTerm,
        isTyping,
        onSearchChange,
        resetSearch,
        suggestions,
        isLoading: isFetching,
        isError,
        isQueryReady,
        selectSuggestion,
        searchData,
    }
}
